import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../config/api';
import PageNavbar from './PageNavbar';
import { formatDateDisplay } from '../utils/formatDate';
import './ExpensesReview.css';

const PAYMENT_METHODS = ['Cash', 'Card', 'UPI', 'Other'];

function formatInr(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function receiptLinks(expense) {
  const paths = expense?.receipt_paths;
  if (Array.isArray(paths) && paths.length) {
    return paths.map((p) => (typeof p === 'string' ? p : p.path)).filter(Boolean);
  }
  if (expense?.bill_file_path) return [expense.bill_file_path];
  return [];
}

export default function ExpensesReview() {
  const [stores, setStores] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [storeId, setStoreId] = useState('');
  const [status, setStatus] = useState('pending');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    employeeName: '',
    reason: '',
    amount: '',
    paymentMethod: 'Cash',
    expenseDate: '',
    reviewNote: ''
  });
  const [saving, setSaving] = useState(false);

  const loadStores = useCallback(async () => {
    try {
      const { data } = await api.get('/stores');
      setStores(data.stores || []);
    } catch {
      setStores([]);
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const params = { limit: 300 };
      if (storeId) params.storeId = storeId;
      if (status) params.status = status;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const { data } = await api.get('/expenses', { params });
      setExpenses(data.expenses || []);
      setSummary(data.summary || {});
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not load expenses' });
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [storeId, status, startDate, endDate]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const openEdit = (expense) => {
    setEditing(expense);
    setForm({
      employeeName: expense.employee_name || '',
      reason: expense.reason || '',
      amount: String(expense.amount ?? ''),
      paymentMethod: PAYMENT_METHODS.includes(expense.payment_method)
        ? expense.payment_method
        : 'Other',
      expenseDate: String(expense.expense_date || '').slice(0, 10),
      reviewNote: expense.review_note || ''
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setSaving(false);
  };

  const saveExpense = async ({ approve, reject } = {}) => {
    if (!editing) return;
    setSaving(true);
    setMsg(null);
    try {
      const body = {
        employeeName: form.employeeName,
        reason: form.reason,
        amount: form.amount,
        paymentMethod: form.paymentMethod,
        expenseDate: form.expenseDate,
        reviewNote: form.reviewNote
      };
      if (approve) body.approve = true;
      if (reject) body.reject = true;
      await api.patch(`/expenses/${editing.id}`, body);
      setMsg({
        type: 'ok',
        text: approve ? 'Expense updated and approved' : reject ? 'Expense rejected' : 'Expense updated'
      });
      closeEdit();
      loadExpenses();
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not save expense' });
      setSaving(false);
    }
  };

  const quickApprove = async (id) => {
    try {
      await api.post(`/expenses/${id}/approve`);
      loadExpenses();
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Approve failed' });
    }
  };

  const statusLabel = useMemo(
    () => ({
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected'
    }),
    []
  );

  return (
    <div className="expenses-review">
      <PageNavbar />
      <div className="container">
        <div className="expenses-review__header">
          <div>
            <h1>POS expenses</h1>
            <p>Review store-logged expenses — approve, reject, or edit then approve.</p>
          </div>
          <div className="expenses-review__summary">
            <div>
              <em>Pending</em>
              <strong>{summary.pending || 0}</strong>
              <span>{formatInr(summary.pendingAmount || 0)}</span>
            </div>
            <div>
              <em>Approved</em>
              <strong>{summary.approved || 0}</strong>
              <span>{formatInr(summary.approvedAmount || 0)}</span>
            </div>
            <div>
              <em>Rejected</em>
              <strong>{summary.rejected || 0}</strong>
              <span>{formatInr(summary.rejectedAmount || 0)}</span>
            </div>
          </div>
        </div>

        {msg ? (
          <div className={`expenses-review__msg expenses-review__msg--${msg.type}`}>{msg.text}</div>
        ) : null}

        <div className="expenses-review__filters">
          <label>
            Store
            <select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
              <option value="">All stores</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </label>
          <label>
            From
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <button type="button" className="btn btn-secondary" onClick={loadExpenses}>
            Refresh
          </button>
        </div>

        <div className="expenses-review__table-wrap">
          {loading ? (
            <div className="expenses-review__empty">Loading…</div>
          ) : expenses.length === 0 ? (
            <div className="expenses-review__empty">No expenses for these filters</div>
          ) : (
            <table className="expenses-review__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Store</th>
                  <th>Employee</th>
                  <th>Purpose</th>
                  <th>Amount</th>
                  <th>Pay</th>
                  <th>Status</th>
                  <th>Receipts</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => {
                  const st = e.status || 'pending';
                  const receipts = receiptLinks(e);
                  return (
                    <tr key={e.id} className={`is-${st}`}>
                      <td>{formatDateDisplay(e.expense_date)}</td>
                      <td>{e.store_name || e.store_code}</td>
                      <td>{e.employee_name || '—'}</td>
                      <td className="expenses-review__purpose" title={e.reason}>
                        {e.reason}
                      </td>
                      <td>{formatInr(e.amount)}</td>
                      <td>{e.payment_method}</td>
                      <td>
                        <span className={`exp-badge exp-badge--${st}`}>{statusLabel[st] || st}</span>
                      </td>
                      <td>
                        {receipts.length
                          ? receipts.map((href, i) => (
                              <a key={`${e.id}-r-${i}`} href={href} target="_blank" rel="noreferrer">
                                #{i + 1}{' '}
                              </a>
                            ))
                          : '—'}
                      </td>
                      <td className="expenses-review__actions">
                        <button type="button" className="btn btn-sm btn-outline" onClick={() => openEdit(e)}>
                          {st === 'pending' ? 'Review' : 'View / edit'}
                        </button>
                        {st === 'pending' ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => quickApprove(e.id)}
                          >
                            Approve
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing ? (
        <div className="expenses-review__modal" onClick={closeEdit}>
          <div className="expenses-review__modal-card" onClick={(ev) => ev.stopPropagation()}>
            <h3>
              Expense · {editing.store_name} · {formatDateDisplay(editing.expense_date)}
            </h3>
            <div className="expenses-review__form">
              <label>
                Employee
                <input
                  value={form.employeeName}
                  onChange={(e) => setForm((f) => ({ ...f, employeeName: e.target.value }))}
                />
              </label>
              <label>
                Purpose
                <textarea
                  rows={3}
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                />
              </label>
              <div className="expenses-review__form-row">
                <label>
                  Amount (₹)
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  />
                </label>
                <label>
                  Payment
                  <select
                    value={form.paymentMethod}
                    onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Date
                  <input
                    type="date"
                    value={form.expenseDate}
                    onChange={(e) => setForm((f) => ({ ...f, expenseDate: e.target.value }))}
                  />
                </label>
              </div>
              <label>
                Review note
                <textarea
                  rows={2}
                  placeholder="Required when rejecting"
                  value={form.reviewNote}
                  onChange={(e) => setForm((f) => ({ ...f, reviewNote: e.target.value }))}
                />
              </label>
              {receiptLinks(editing).length ? (
                <div className="expenses-review__receipts">
                  {receiptLinks(editing).map((href, i) => (
                    <a key={i} href={href} target="_blank" rel="noreferrer">
                      Receipt {i + 1}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="expenses-review__modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeEdit} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-outline" onClick={() => saveExpense()} disabled={saving}>
                Save only
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => saveExpense({ reject: true })}
                disabled={saving}
              >
                Reject
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => saveExpense({ approve: true })}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Edit & approve'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
